import { Link } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { Banner } from '../components/ui';
import { useDocumentMeta } from '../components/Prose';
import { useSession } from '../state/session';
import { SUPPORT, type SupportChannel } from '../content/support';

/** Who the answer comes from, said in one word before the detail. */
const ROUTE: Record<SupportChannel['action'], { label: string; tone: string }> = {
  email: { label: 'Write to us', tone: 'pill--brand' },
  sender: { label: 'Ask the sender', tone: 'pill--warn' },
  self: { label: 'Do it yourself', tone: 'pill--ok' },
};

function Channel({ channel, email }: { channel: SupportChannel; email: string }) {
  const route = ROUTE[channel.action];
  return (
    <div className="card stack-s">
      <div className="row row--tight">
        <span className={`pill ${route.tone}`}>{route.label}</span>
        <span className="small muted">{channel.audience}</span>
      </div>
      <strong style={{ fontSize: '.98rem', lineHeight: 1.35 }}>{channel.when}</strong>
      <p className="small muted" style={{ margin: 0, lineHeight: 1.65 }}>
        {channel.detail}
      </p>
      {channel.cta && (
        <Link className="btn btn--small" to={channel.cta.to}>{channel.cta.label}</Link>
      )}
      {channel.action === 'email' && (
        <a className="btn btn--small btn--ghost" href={`mailto:${email}`}>
          {email}
        </a>
      )}
    </div>
  );
}

export function Support() {
  const { user } = useSession();
  useDocumentMeta(`${SUPPORT.title} — eSignLoop`, SUPPORT.description);

  return (
    <AppShell
      nav={[
        { to: '/pricing', label: 'Pricing' },
        { to: '/blog', label: 'Blog' },
        { to: '/verify', label: 'Verify a document' },
      ]}
      actions={user
        ? <Link className="btn btn--primary btn--small" to="/app">Dashboard</Link>
        : <Link className="btn btn--primary btn--small" to="/register">Sign up</Link>}
    >
      <section className="shell section">
        <header style={{ maxWidth: '62ch' }}>
          <h1 style={{ margin: 0 }}>{SUPPORT.title}</h1>
          <p className="hero__lede" style={{ marginTop: 12 }}>
            {SUPPORT.description}
          </p>
          <p className="muted" style={{ marginTop: 12 }}>
            One address for everything:{' '}
            <a href={`mailto:${SUPPORT.email}`}><strong>{SUPPORT.email}</strong></a>.
            Find your situation below first — two of the commonest questions are
            not ones we can answer.
          </p>
        </header>

        <div className="grid grid--cards" style={{ marginTop: 28 }}>
          {SUPPORT.channels.map((channel, index) => (
            <Channel channel={channel} email={SUPPORT.email} key={index} />
          ))}
        </div>

        <div className="grid grid--two" style={{ marginTop: 28, gap: 24 }}>
          <div className="card stack-s">
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>What to include</h2>
            <ul className="prose__list" style={{ margin: 0 }}>
              {SUPPORT.include.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          </div>
          <div className="card stack-s">
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>What never to send us</h2>
            <ul className="prose__list" style={{ margin: 0 }}>
              {SUPPORT.never.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
            <p className="small muted" style={{ margin: 0 }}>
              We will never ask you for any of these, and a message that does
              is not from us.
            </p>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <Banner tone="info" title="How quickly we reply">
            {SUPPORT.expectations}
          </Banner>
        </div>

        <p className="small muted" style={{ marginTop: 24 }}>
          Looking for the rules rather than a person?{' '}
          <Link to="/terms">Terms of Service</Link> ·{' '}
          <Link to="/privacy">Privacy Policy</Link> ·{' '}
          <Link to="/blog">Writing</Link>
        </p>
      </section>
    </AppShell>
  );
}
